// Shared score composition for every sport's multiplicative chain (audit 92.4(b),
// 92.6 #1; tracker P3-19).
//
// Every scorer declares its chain once as data (an ordered list of typed factors)
// and routes its final assembly through composeScore(). The factor classes encode
// the scoring philosophy's tiers:
//
//   driver     - quality driver: carries the score and may legitimately tank it
//                (swell base, wind for wind sports, snow quality, ski flat light).
//   modifier   - enjoyment modifier: bounded multiplier that per the philosophy
//                floors around 0.85 ("a great swell on a grey day is still a
//                great day").
//   structural - not a condition factor: scale constants, bounded bonuses
//                (golden hour), static per-spot constants, provenance discounts.
//
// Classes are ENFORCED here as of Phase 7 (tracker P7-1, audit 92.4(b) / 92.6 #1).
// composeScore is the single place the philosophy's two-tier structure is enforced
// "by construction instead of convention" (the audit's headline lesson):
//
//     score = prod(drivers, structural) * M,   M = max(prod(modifiers), M_FLOOR)
//
// where each enjoyment modifier is floored at MODIFIER_FLOOR (~0.85: "a great swell
// on a grey day is still a great day") and the modifier sub-product is floored at
// MODIFIER_PRODUCT_FLOOR (~0.80) so a stack of modifiers can never tank a score
// (the floor-stacking pathology the audit names, e.g. SUP precip x cloud x WQ ->
// 0.23). Gamma then compresses the whole floored product, exactly as before
// (P6-1's full-product gamma is preserved; Phase 7 does NOT move gamma onto the
// driver core, which would re-open Phase 6). A modifier whose value exceeds
// MODIFIER_CEILING is a misclassification (it is really a structural bonus) and
// throws in dev/test - the "out-of-bounds modifier = test failure" guard (P7-20);
// production clamps to the band defensively. The floor is the structural low-side
// guarantee, so a sub-0.85 modifier is NOT an error, it is clamped up.
//
// This is NOT behaviour-neutral (unlike the P3-19 conversion): it moves real
// scores wherever a factor classed "modifier" dipped below the floor (every rain
// and coastal-visibility breach the audit catalogued). Drivers and structural
// terms are unchanged: they multiply into the base exactly as the hand-typed
// expressions did. Clamping, rounding and scale conversion stay in each scorer
// because their order is per-sport display semantics and load-bearing.
//
// Active sport chains pin their calibrated GammaParams. The historical "auto"
// policy remains supported for compatibility and experiments. Adding a neutral
// factor must not select a new calibration curve for a production sport.
// Sailing is the documented additive-core exemption, with gamma null.

import { adaptiveGamma, presetForChainLength, type GammaParams } from "./gamma";

export type FactorClass = "driver" | "modifier" | "structural";

/**
 * Enjoyment-modifier bounds (tracker P7-1, audit clause 3 / 92.4(b)).
 *   MODIFIER_FLOOR          each modifier floors here ("modulate but never tank").
 *   MODIFIER_PRODUCT_FLOOR  the product of all a chain's modifiers floors here,
 *                           so stacked modifiers cannot compound into a tank.
 *   MODIFIER_CEILING        a modifier may carry a bounded micro-bonus (overcast
 *                           sky 1.015, clear-sky swim sun 1.04) but no more; a
 *                           value above this is a misclassified structural bonus.
 */
export const MODIFIER_FLOOR = 0.85;
export const MODIFIER_PRODUCT_FLOOR = 0.8;
export const MODIFIER_CEILING = 1.05;

/**
 * A chain's gamma policy:
 *   "auto"      derive the preset from declaredChainLength (the P6-1 policy);
 *   null        documented exemption (no compression);
 *   GammaParams explicit preset (forecast proxies pinned to their parent).
 */
export type GammaPolicy = GammaParams | "auto" | null;

export interface FactorSpec<K extends string = string> {
  readonly key: K;
  readonly class: FactorClass;
  /** Classification context; contested classes name the Phase 7 tracker item. */
  readonly note?: string;
}

export interface ChainSpec<K extends string = string> {
  /** Sport or chain identifier, e.g. "surf", "prone-foil-forecast-proxy". */
  readonly sport: string;
  /**
   * Gamma policy applied to the chain product (0-1 domain). "auto" derives the
   * preset from the chain's declared length (P6-1); null is a documented
   * exemption; an explicit GammaParams pins the preset. See GammaPolicy.
   */
  readonly gamma: GammaPolicy;
  /** Ordered terms; multiplication is exactly left to right over this list. */
  readonly factors: readonly FactorSpec<K>[];
}

export interface ComposedScore {
  /** Raw left-to-right product of the chain's terms (pre-gamma). */
  readonly product: number;
  /** Product after the chain's gamma preset; equals product when gamma is null. */
  readonly compressed: number;
}

/** Identity helper that pins the factor keys as literal types, so composeScore
 *  callers get compile-time exhaustiveness on the values record. */
export function defineChain<const K extends string>(chain: ChainSpec<K>): ChainSpec<K> {
  return chain;
}

/**
 * Resolve a chain's gamma policy to a concrete preset (or null for no
 * compression). "auto" derives the preset from the declared length (P6-1).
 */
export function resolveGamma(chain: ChainSpec): GammaParams | null {
  if (chain.gamma === "auto") return presetForChainLength(declaredChainLength(chain));
  return chain.gamma;
}

const ENFORCE_MODIFIER_BOUNDS =
  typeof process !== "undefined" && process.env.NODE_ENV !== "production";

/**
 * Compose the chain: drivers and structural terms multiply into the base; the
 * enjoyment modifiers are each floored at MODIFIER_FLOOR and their product is
 * floored at MODIFIER_PRODUCT_FLOOR (so they modulate but never tank); the floored
 * modifier product multiplies the base; then the chain's gamma preset compresses
 * the whole product (P6-1 full-product gamma, unchanged). The caller owns every
 * clamp, round and scale step after this, exactly as before.
 *
 * Enforcement (P7-1): a modifier above MODIFIER_CEILING is a misclassified
 * structural bonus and throws in dev/test (the P7-20 guard); production clamps to
 * the band. The floor is the low-side guarantee, so a sub-floor modifier is
 * clamped up, never an error.
 */
export function composeScore<K extends string>(
  chain: ChainSpec<K>,
  values: Readonly<Record<K, number>>,
): ComposedScore {
  let base = 1;
  let modifierProduct = 1;
  let hasModifier = false;
  for (const factor of chain.factors) {
    const v = values[factor.key];
    if (factor.class === "modifier") {
      hasModifier = true;
      if (ENFORCE_MODIFIER_BOUNDS && (Number.isNaN(v) || v > MODIFIER_CEILING)) {
        throw new Error(
          `composeScore: modifier "${factor.key}" of chain "${chain.sport}" produced ${v}, ` +
            `outside the enjoyment-modifier band [0, ${MODIFIER_CEILING}]. A modifier must be a ` +
            `bounded enjoyment multiplier; reclassify it as a driver (if it degrades the medium) ` +
            `or structural (if it is a bonus).`,
        );
      }
      // Floor each modifier (low side is the enforced guarantee); cap at the
      // ceiling defensively so a misclassification cannot inflate in production.
      modifierProduct *= v < MODIFIER_FLOOR ? MODIFIER_FLOOR : v > MODIFIER_CEILING ? MODIFIER_CEILING : v;
    } else {
      base *= v;
    }
  }
  const m = hasModifier ? Math.max(modifierProduct, MODIFIER_PRODUCT_FLOOR) : 1;
  const product = base * m;
  const preset = resolveGamma(chain);
  const compressed = preset === null ? product : adaptiveGamma(product, preset);
  return { product, compressed };
}

/**
 * Declared condition-factor count (drivers + modifiers; structural terms are
 * not condition factors). Phase 6 derives each chain's gamma preset from this
 * instead of a hand-picked preset.
 */
export function declaredChainLength(chain: ChainSpec): number {
  return chain.factors.filter((f) => f.class !== "structural").length;
}
